// A themed presentation of the existing native product option; variant logic stays native.
if (!customElements.get('scc-variant-dropdown')) {
  customElements.define('scc-variant-dropdown', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const events = { signal: this.abort.signal };
      this.select = this.querySelector('select');
      if (!this.select || !this.select.options.length) return;
      this.trigger = this.querySelector('.scc-variant-dropdown__trigger');
      if (!this.trigger) {
        this.trigger = document.createElement('button');
        this.trigger.type = 'button';
        this.trigger.id = this.select.id + '-trigger';
        this.trigger.className = 'form-control form-control--select scc-variant-dropdown__trigger';
        this.trigger.setAttribute('aria-haspopup', 'listbox');
        this.trigger.setAttribute('aria-controls', this.select.id + '-list');
        this.list = document.createElement('div');
        this.list.id = this.select.id + '-list';
        this.list.className = 'scc-variant-dropdown__list';
        this.list.setAttribute('role', 'listbox');
        this.list.setAttribute('aria-label', this.dataset.optionName || 'Option');
        this.list.hidden = true;
        [...this.select.options].forEach((option, index) => {
          const item = document.createElement('button');
          item.type = 'button';
          item.className = 'scc-variant-dropdown__option';
          item.setAttribute('role', 'option');
          item.tabIndex = -1;
          item.dataset.index = index;
          item.textContent = option.textContent.trim();
          item.disabled = option.disabled;
          this.list.append(item);
        });
        this.append(this.trigger, this.list);
      } else {
        this.list = this.querySelector('.scc-variant-dropdown__list');
      }
      this.items = [...this.list.children];
      const label = this.closest('.product-form__input')?.querySelector('label.form__label, label');
      if (label?.htmlFor === this.select.id) label.htmlFor = this.trigger.id;
      this.select.hidden = true;
      this.select.tabIndex = -1;
      this.select.setAttribute('aria-hidden', 'true');
      this.dataset.enhanced = '';
      this.close();
      this.sync();
      this.trigger.addEventListener('click', () => this.list.hidden ? this.open() : this.close(), events);
      this.list.addEventListener('click', event => {
        const item = event.target.closest('[role="option"]');
        if (!item || item.disabled) return;
        const index = Number(item.dataset.index);
        const changed = this.select.selectedIndex !== index;
        this.select.value = this.select.options[index].value;
        this.sync();
        this.close(true);
        if (changed) this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }, events);
      this.select.addEventListener('change', () => this.sync(), events);
      this.addEventListener('keydown', event => this.onKeydown(event), events);
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.close();
      }, events);
      this.addEventListener('focusout', event => {
        if (!this.contains(event.relatedTarget)) this.close();
      }, events);
    }
    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.typeTimer);
    }
    sync() {
      this.trigger.textContent = this.select.selectedOptions[0]?.textContent.trim() || '';
      this.trigger.disabled = this.select.disabled;
      this.trigger.setAttribute('aria-label', `${this.dataset.optionName || 'Option'}: ${this.trigger.textContent}`);
      this.items.forEach((item, index) => item.setAttribute('aria-selected', String(index === this.select.selectedIndex)));
    }
    open() {
      this.list.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.dataset.open = '';
      const item = this.items[this.select.selectedIndex];
      (item?.disabled ? this.items.find(item => !item.disabled) : item)?.focus({ preventScroll: true });
      item?.scrollIntoView({ block: 'nearest' });
    }
    close(restoreFocus = false) {
      this.list.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      delete this.dataset.open;
      if (restoreFocus) this.trigger.focus({ preventScroll: true });
    }
    onKeydown(event) {
      if (event.key === 'Escape') {
        if (!this.list.hidden) { event.preventDefault(); event.stopPropagation(); this.close(true); }
        return;
      }
      if (event.key === 'Tab') { this.close(); return; }
      const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
      if (keys.includes(event.key)) {
        event.preventDefault();
        if (this.list.hidden) { this.open(); return; }
        const enabled = this.items.filter(item => !item.disabled);
        const index = enabled.indexOf(document.activeElement);
        let next = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1 :
          Math.max(0, Math.min(enabled.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)));
        enabled[next]?.focus({ preventScroll: true });
        enabled[next]?.scrollIntoView({ block: 'nearest' });
      } else if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        if (this.list.hidden) this.open();
        this.typed = (this.typed || '') + event.key.toLocaleLowerCase();
        clearTimeout(this.typeTimer);
        this.typeTimer = setTimeout(() => { this.typed = ''; }, 700);
        const item = this.items.find(item => !item.disabled && item.textContent.toLocaleLowerCase().startsWith(this.typed));
        item?.focus({ preventScroll: true });
        item?.scrollIntoView({ block: 'nearest' });
      }
    }
  });
}
